import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DateRangePicker } from '../components/common';
import { ROUTES } from '../constants';
import { ApiError, earningsBonusesService } from '../services';

const DEFAULT_MARKETER = {
  id: '3fe908619dAe3782b',
  accountId: '3fe908619dAe3782b',
  firstName: 'John',
  lastName: 'Doe',
};

const INITIAL_FILTERS = {
  searchName: '',
  period: 'all',
  channel: '',
  marketingBoostType: '',
  transactionType: '',
  dateFrom: '',
  dateTo: '',
};

const INITIAL_PAGINATION = {
  page: 1,
  pageCount: 1,
  size: 20,
  count: 0,
  total: 0,
};

const CHANNEL_OPTIONS = [
  { value: '', label: 'All Channel' },
  { value: '1Snap', label: '1Snap' },
  { value: '1Tap', label: '1Tap' },
  { value: 'Manual Transaction', label: 'Manual Transaction' },
];

const MARKETING_BOOST_TYPE_OPTIONS = [
  { value: '', label: 'All Type' },
  { value: 'Merchant Onboarding', label: 'Merchant Onboarding' },
  { value: 'Merchant Voucher Code', label: 'Merchant Voucher Code' },
  { value: 'Recommender Commission', label: 'Recommender Commission' },
  { value: 'Shopper Transaction', label: 'Shopper Transaction' },
];

const VOLUME_POINT_TYPE_OPTIONS = [
  { value: '', label: 'All Type' },
  { value: 'Subscriptions', label: 'Subscriptions' },
  { value: 'Merchant Voucher Code', label: 'Merchant Voucher Code' },
  { value: 'Purchase', label: 'Purchase' },
];

const safeText = value => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatDateTime = value => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatCurrencyEUR = value => {
  if (value === null || value === undefined || value === '') return '-';

  const number = Number(value);
  if (!Number.isFinite(number)) return '-';

  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatNumber = value => {
  if (value === null || value === undefined || value === '') return '-';

  const number = Number(value);
  if (!Number.isFinite(number)) return '-';

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(number);
};

const formatPercentage = value => {
  if (value === null || value === undefined || value === '') return '-';

  const number = Number(value);
  if (!Number.isFinite(number)) return '-';

  return `${number.toFixed(2)}%`;
};

const getResponseMessage = response =>
  response?.data?.message ||
  response?.message ||
  'Your export is being processed. The file will be sent to your registered email address shortly.';

const getPageNumbers = pagination =>
  Array.from(
    new Set(
      [
        1,
        pagination.page - 1,
        pagination.page,
        pagination.page + 1,
        pagination.pageCount,
      ].filter(page => page >= 1 && page <= pagination.pageCount)
    )
  );

const MarketerEarningsBonusesPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const marketer = useMemo(() => {
    const stateMarketer = location.state?.marketer || {};

    return {
      ...DEFAULT_MARKETER,
      ...stateMarketer,
      id: stateMarketer.id || id || DEFAULT_MARKETER.id,
      accountId:
        stateMarketer.accountId ||
        stateMarketer.id ||
        id ||
        DEFAULT_MARKETER.accountId,
    };
  }, [id, location.state]);

  const fullName =
    marketer.fullName ||
    [marketer.firstName, marketer.lastName].filter(Boolean).join(' ') ||
    'Creator';

  const [activeTab, setActiveTab] = useState('marketingBoost');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [searchFocused, setSearchFocused] = useState(false);
  const [shoppingCycles, setShoppingCycles] = useState([]);
  const [marketingBoostRows, setMarketingBoostRows] = useState([]);
  const [volumePointRows, setVolumePointRows] = useState([]);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [exportResult, setExportResult] = useState(null);
  const messageBannerRef = useRef(null);

  const activeRows =
    activeTab === 'marketingBoost' ? marketingBoostRows : volumePointRows;

  const displayStart =
    pagination.count > 0 ? (pagination.page - 1) * pagination.size + 1 : 0;
  const displayEnd =
    pagination.count > 0
      ? Math.min(pagination.page * pagination.size, pagination.count)
      : 0;
  const pageNumbers = getPageNumbers(pagination);

  const buildParams = useCallback(
    (tab, sourceFilters, page = 1, includePagination = true) => {
      const params = {
        shoppingCycleCode: sourceFilters.period,
        dateFrom: sourceFilters.dateFrom,
        dateTo: sourceFilters.dateTo,
      };

      if (includePagination) {
        params.page = page;
        params.size = pagination.size;
      }

      if (tab === 'volumePoint') {
        params.keyword = sourceFilters.searchName;
        params.type = sourceFilters.transactionType;
      } else {
        params.channel = sourceFilters.channel;
        params.type = sourceFilters.marketingBoostType;
      }

      return params;
    },
    [pagination.size]
  );

  const fetchRows = useCallback(
    async ({
      page = 1,
      tab = activeTab,
      sourceFilters = appliedFilters,
    } = {}) => {
      setLoading(true);
      setError(null);

      try {
        const params = buildParams(tab, sourceFilters, page);
        const response =
          tab === 'volumePoint'
            ? await earningsBonusesService.getCreatorVolumePoint(
                marketer.id,
                params
              )
            : await earningsBonusesService.getCreatorMarketingBoost(
                marketer.id,
                params
              );

        const data = response?.data || {};
        const rows = Array.isArray(data.data) ? data.data : [];

        if (tab === 'volumePoint') {
          setVolumePointRows(rows);
        } else {
          setMarketingBoostRows(rows);
        }

        setPagination({
          page: data.page || page,
          pageCount: data.pageCount || 1,
          size: data.size || pagination.size,
          count: data.count || 0,
          total: data.total || 0,
        });
      } catch (err) {
        console.error('Failed to fetch creator earnings:', err);
        if (tab === 'volumePoint') {
          setVolumePointRows([]);
        } else {
          setMarketingBoostRows([]);
        }
        setPagination(prev => ({
          ...prev,
          page,
          pageCount: 1,
          count: 0,
          total: 0,
        }));

        if (err instanceof ApiError) {
          setError(`Failed to load earnings: ${err.message}`);
        } else {
          setError('Failed to load earnings. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    },
    [activeTab, appliedFilters, buildParams, marketer.id, pagination.size]
  );

  const loadShoppingCycles = useCallback(async () => {
    try {
      const response = await earningsBonusesService.getShoppingCycles(4);
      setShoppingCycles(response.data || []);
    } catch (err) {
      console.warn('Failed to load shopping cycle options:', err);
    }
  }, []);

  useEffect(() => {
    loadShoppingCycles();
  }, [loadShoppingCycles]);

  useEffect(() => {
    fetchRows({ page: 1, tab: activeTab, sourceFilters: appliedFilters });
  }, [activeTab, appliedFilters, fetchRows]);

  useEffect(() => {
    if ((error || exportResult) && messageBannerRef.current) {
      messageBannerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [error, exportResult]);

  const handleFilterChange = field => event => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handlePeriodChange = event => {
    setFilters(prev => ({
      ...prev,
      period: event.target.value,
      dateFrom: '',
      dateTo: '',
    }));
  };

  const handleDateRangeChange = useCallback(({ startDate, endDate }) => {
    setFilters(prev => ({
      ...prev,
      period: startDate || endDate ? 'all' : prev.period,
      dateFrom: startDate || '',
      dateTo: endDate || '',
    }));
  }, []);

  const clearFilters = () => {
    setExportResult(null);
    setFilters({ ...INITIAL_FILTERS });
    setAppliedFilters({ ...INITIAL_FILTERS });
  };

  const applyFilters = () => {
    setExportResult(null);
    setAppliedFilters({ ...filters });
  };

  const handlePageChange = newPage => {
    if (
      loading ||
      newPage < 1 ||
      newPage > pagination.pageCount ||
      newPage === pagination.page
    ) {
      return;
    }

    fetchRows({
      page: newPage,
      tab: activeTab,
      sourceFilters: appliedFilters,
    });
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    setExportResult(null);

    try {
      const params = buildParams(activeTab, appliedFilters, 1, false);
      const response =
        activeTab === 'volumePoint'
          ? await earningsBonusesService.exportCreatorVolumePoint(
              marketer.id,
              params
            )
          : await earningsBonusesService.exportCreatorMarketingBoost(
              marketer.id,
              params
            );

      setExportResult({ message: getResponseMessage(response) });
    } catch (err) {
      console.error('Failed to export creator earnings:', err);
      if (err instanceof ApiError) {
        setError(`Failed to export earnings: ${err.message}`);
      } else {
        setError('Failed to export earnings. Please try again.');
      }
    } finally {
      setExporting(false);
    }
  };

  const goBack = () => {
    navigate(
      ROUTES.generatePath(ROUTES.MARKETERS_DETAIL_LIST, { id: marketer.id }),
      {
        state: {
          marketer,
          accountType: location.state?.accountType || 'M',
        },
      }
    );
  };

  const BackIcon = ({ className = 'w-5 h-5' }) => (
    <svg
      className={className}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path strokeLinecap='round' strokeLinejoin='round' d='M15 19l-7-7 7-7' />
      <path strokeLinecap='round' strokeLinejoin='round' d='M8 12h13' />
    </svg>
  );

  const SearchIcon = ({ className = 'w-5 h-5' }) => (
    <svg
      className={className}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z'
      />
    </svg>
  );

  const SelectField = ({ id: fieldId, label, value, onChange, children }) => (
    <div>
      <label
        htmlFor={fieldId}
        className='block text-sm font-medium text-gray-900 mb-2'
      >
        {label}
      </label>
      <select
        id={fieldId}
        value={value}
        onChange={onChange}
        disabled={loading}
        className='block w-full px-3 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500'
      >
        {children}
      </select>
    </div>
  );

  const DateRangeField = () => (
    <DateRangePicker
      startDate={filters.dateFrom}
      endDate={filters.dateTo}
      onDateRangeChange={handleDateRangeChange}
      disabled={loading}
      placeholder='Select date range'
      label={{
        start: 'Transaction Date From',
        end: 'Transaction Date To',
      }}
      className='xl:col-span-3'
    />
  );

  const ShoppingCycleOptions = () => (
    <>
      <option value='all'>All Period</option>
      <option value='current'>Current Cycle</option>
      {shoppingCycles.slice(1).map(cycle => (
        <option key={cycle.id || cycle.code} value={cycle.code}>
          {cycle.name || cycle.code}
        </option>
      ))}
    </>
  );

  const renderFilterFields = () => {
    if (activeTab === 'volumePoint') {
      return (
        <>
          <div className='xl:col-span-3'>
            <label
              htmlFor='name-search'
              className='block text-sm font-medium text-gray-900 mb-2'
            >
              Search Name
            </label>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <SearchIcon
                  className={`w-5 h-5 ${
                    searchFocused ? 'text-blue-500' : 'text-gray-400'
                  }`}
                />
              </div>
              <input
                id='name-search'
                type='text'
                value={filters.searchName}
                onChange={handleFilterChange('searchName')}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder='Search by name (min 3 chars)...'
                disabled={loading}
                className='block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500'
              />
            </div>
          </div>
          <div className='xl:col-span-3'>
            <SelectField
              id='vp-shopping-cycle-period'
              label='Shopping Cycle Period'
              value={filters.period}
              onChange={handlePeriodChange}
            >
              <ShoppingCycleOptions />
            </SelectField>
          </div>
          <div className='xl:col-span-3'>
            <SelectField
              id='vp-transaction-type'
              label='Transaction Type'
              value={filters.transactionType}
              onChange={handleFilterChange('transactionType')}
            >
              {VOLUME_POINT_TYPE_OPTIONS.map(option => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>
        </>
      );
    }

    return (
      <>
        <div className='xl:col-span-3'>
          <SelectField
            id='mb-shopping-cycle-period'
            label='Shopping Cycle Period'
            value={filters.period}
            onChange={handlePeriodChange}
          >
            <ShoppingCycleOptions />
          </SelectField>
        </div>
        <div className='xl:col-span-3'>
          <SelectField
            id='transaction-channel'
            label='Transaction Channel'
            value={filters.channel}
            onChange={handleFilterChange('channel')}
          >
            {CHANNEL_OPTIONS.map(option => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>
        </div>
        <div className='xl:col-span-3'>
          <SelectField
            id='marketing-boost-type'
            label='Marketing Boost Type'
            value={filters.marketingBoostType}
            onChange={handleFilterChange('marketingBoostType')}
          >
            {MARKETING_BOOST_TYPE_OPTIONS.map(option => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>
        </div>
      </>
    );
  };

  const renderPagination = () => {
    if (pagination.count === 0) return null;

    return (
      <div className='px-4 md:px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
        <p className='text-sm text-gray-700'>
          Page <span className='font-medium'>{pagination.page}</span> of{' '}
          <span className='font-medium'>{pagination.pageCount}</span>
        </p>
        <nav
          className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
          aria-label='Pagination'
        >
          <button
            type='button'
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={loading || pagination.page <= 1}
            className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
          >
            <span className='sr-only'>Previous</span>
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                clipRule='evenodd'
              />
            </svg>
          </button>
          {pageNumbers.map(num => (
            <button
              key={num}
              type='button'
              onClick={() => handlePageChange(num)}
              disabled={loading}
              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                num === pagination.page
                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
              } disabled:opacity-50`}
            >
              {num}
            </button>
          ))}
          <button
            type='button'
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={loading || pagination.page >= pagination.pageCount}
            className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
          >
            <span className='sr-only'>Next</span>
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                clipRule='evenodd'
              />
            </svg>
          </button>
        </nav>
      </div>
    );
  };

  const renderMarketingBoostTable = () => (
    <div className='overflow-x-auto'>
      <table className='min-w-full divide-y divide-gray-200'>
        <thead className='bg-gray-50'>
          <tr>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Transaction Date
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Name
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Transaction Status
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Last Updated
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Marketing Boost Type
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Marketing Boost Received
            </th>
          </tr>
        </thead>
        <tbody className='bg-white divide-y divide-gray-200'>
          {loading && marketingBoostRows.length === 0 && (
            <tr>
              <td
                colSpan={6}
                className='px-4 py-8 text-center text-sm text-gray-500'
              >
                Loading marketing boost...
              </td>
            </tr>
          )}

          {!loading && marketingBoostRows.length === 0 && (
            <tr>
              <td
                colSpan={6}
                className='px-4 py-8 text-center text-sm text-gray-500'
              >
                No marketing boost found.
              </td>
            </tr>
          )}

          {marketingBoostRows.map(row => (
            <tr key={row.id} className='hover:bg-gray-50 align-top'>
              <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                {formatDateTime(row.transactionDate)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 min-w-[180px]'>
                {safeText(row.name)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900'>
                {safeText(row.status)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                {formatDateTime(row.lastUpdated)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 min-w-[180px]'>
                {safeText(row.marketingBoostType)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                {formatCurrencyEUR(row.marketingBoostReceived)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderVolumePointTable = () => (
    <div className='overflow-x-auto'>
      <table className='min-w-full divide-y divide-gray-200'>
        <thead className='bg-gray-50'>
          <tr>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Transaction Date
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Name
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Transaction Status
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Last Updated
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              VP Source
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Transaction Type
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              VP Percentage
            </th>
            <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              VP Received
            </th>
          </tr>
        </thead>
        <tbody className='bg-white divide-y divide-gray-200'>
          {loading && volumePointRows.length === 0 && (
            <tr>
              <td
                colSpan={8}
                className='px-4 py-8 text-center text-sm text-gray-500'
              >
                Loading volume point...
              </td>
            </tr>
          )}

          {!loading && volumePointRows.length === 0 && (
            <tr>
              <td
                colSpan={8}
                className='px-4 py-8 text-center text-sm text-gray-500'
              >
                No volume point found.
              </td>
            </tr>
          )}

          {volumePointRows.map(row => (
            <tr key={row.id} className='hover:bg-gray-50 align-top'>
              <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                {formatDateTime(row.transactionDate)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 min-w-[180px]'>
                {safeText(row.name)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900'>
                {safeText(row.status)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                {formatDateTime(row.lastUpdated)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 min-w-[150px]'>
                {safeText(row.vpSource)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 min-w-[150px]'>
                {safeText(row.transactionType)}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                {formatPercentage(
                  row.vpPercentage
                )}
              </td>
              <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                {formatNumber(row.vpReceived)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className='space-y-6'>
      <button
        type='button'
        onClick={goBack}
        className='inline-flex items-center gap-2 px-4 py-3 border border-gray-400 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
      >
        <BackIcon />
        Back
      </button>

      <div className='bg-white shadow-sm border-b border-gray-200 px-6 py-7'>
        <h1 className='text-3xl font-bold text-gray-900'>Earnings & Bonuses</h1>
        <p className='mt-2 text-base text-gray-500'>
          Displaying transaction earning & bonuses history information
        </p>
      </div>

      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4 xl:items-end'>
          {renderFilterFields()}

          <div className='xl:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <button
              type='button'
              onClick={clearFilters}
              disabled={loading}
              className='w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-60'
            >
              Clear Filters
            </button>
            <button
              type='button'
              onClick={applyFilters}
              disabled={loading}
              className='w-full px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60'
            >
              Apply Filters
            </button>
          </div>

          <DateRangeField />
        </div>
      </div>

      {/* Error & Export Result Display */}
      {(error || exportResult) && (
        <div ref={messageBannerRef}>
          {error && (
            <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
              <div className='flex items-center'>
                <svg
                  className='w-5 h-5 text-red-400 mr-2'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
                <p className='text-sm text-red-700'>{error}</p>
                <button
                  onClick={() => setError(null)}
                  className='ml-auto text-red-400 hover:text-red-600'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {exportResult && (
            <div className='bg-green-50 border border-green-200 rounded-lg p-4'>
              <div className='flex items-start'>
                <svg
                  className='w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                <div className='flex-1 min-w-0'>
                  <h4 className='text-sm font-semibold text-green-800 mb-1'>
                    Export Successful
                  </h4>
                  <p className='text-sm text-green-700'>
                    {exportResult.message}
                  </p>
                </div>
                <button
                  onClick={() => setExportResult(null)}
                  className='ml-auto text-green-400 hover:text-green-600'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
          <div>
            <h2 className='text-2xl font-bold text-gray-800 flex items-center gap-2'>
              {fullName}
              {loading && (
                <svg
                  className='animate-spin h-5 w-5 text-blue-600'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              )}
            </h2>
            <p className='text-sm text-gray-500 mt-2 break-all'>
              Creator ID: {marketer.id}
            </p>
            {pagination.count > 0 && (
              <p className='text-sm text-gray-500 mt-1'>
                Showing {displayStart} to {displayEnd} of {pagination.count}{' '}
                records
              </p>
            )}
          </div>
          <button
            type='button'
            onClick={handleExport}
            disabled={exporting || loading || activeRows.length === 0}
            className='inline-flex justify-center px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 md:min-w-[180px] disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {exporting ? 'Exporting...' : 'Export Data'}
          </button>
        </div>

        <div className='px-4 md:px-6 border-b border-gray-200'>
          <nav className='flex gap-6 -mb-px' aria-label='Earnings tabs'>
            <button
              type='button'
              onClick={() => setActiveTab('marketingBoost')}
              className={`px-2 py-3 border-b-2 text-sm font-semibold ${
                activeTab === 'marketingBoost'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Marketing Boost
            </button>
            <button
              type='button'
              onClick={() => setActiveTab('volumePoint')}
              className={`px-2 py-3 border-b-2 text-sm font-semibold ${
                activeTab === 'volumePoint'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Volume Point (VP)
            </button>
          </nav>
        </div>

        {activeTab === 'marketingBoost'
          ? renderMarketingBoostTable()
          : renderVolumePointTable()}

        {renderPagination()}
      </div>
    </div>
  );
};

export default MarketerEarningsBonusesPage;
